// Common Service Functions
import { buildURL } from './commons/CommonService.Functions';
import authFetch from './httpClient';

// Models
import { Service, NewService, ServiceExportFilter } from '../models/Entities/Service';
import { IResult } from '../models/HttpResult';
import * as Sentry from '@sentry/react';

// Base URL
const BaseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'protocols/services';
const ProtocolBaseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'protocols/protocols';

const request = async <T>(url: string, options: RequestInit, context: string): Promise<T> => {
  try {
    const response = await authFetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> | undefined)
      }
    });

    if (!response.ok) {
      Sentry.captureException(response);
      throw response;
    }

    const data: IResult<T> = await response.json();
    return data.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

/**
 * Get all services
 * @returns Promise with array of services
 */
export const GetAllServices = async (): Promise<Service[]> => {
  return request<Service[]>(BaseURL, { method: 'GET' }, 'GetAllServices');
};

/**
 * Get service by id
 * @param id - Service id
 * @returns Promise with service data
 */
export const GetServiceById = async (id: string): Promise<Service> => {
  return request<Service>(buildURL(BaseURL, id), { method: 'GET' }, 'GetServiceById');
};

/**
 * Get services by consumer id
 * @param consumerId - Consumer id
 * @returns Promise with array of services
 */
export const GetServicesByConsumerId = async (
  consumerId: string
): Promise<Service[]> => {
  const baseConsumerUrl = process.env.REACT_APP_BILLER_API_GATEWAY + 'protocols/consumers';
  return request<Service[]>(
    buildURL(baseConsumerUrl, consumerId, 'services'),
    { method: 'GET' },
    'GetServicesByConsumerId'
  );
};

/**
 * Create a new service
 * @param service - New service data
 * @returns Promise with created service
 */
export const CreateService = async (
  service: NewService
): Promise<Service> => {
  return request<Service>(
    BaseURL,
    {
      method: 'POST',
      body: JSON.stringify(service)
    },
    'CreateService'
  );
};

/**
 * Update an existing service
 * @param id - Service id
 * @param service - Updated service data
 * @returns Promise with updated service
 */
export const UpdateService = async (
  id: string,
  service: NewService
): Promise<Service> => {
  return request<Service>(
    buildURL(BaseURL, id),
    {
      method: 'PUT',
      body: JSON.stringify(service)
    },
    'UpdateService'
  );
};

/**
 * Delete a service
 * @param id - Service id
 * @returns Promise with deleted service id
 */
export const DeleteService = async (id: string): Promise<string> => {
  return request<string>(buildURL(BaseURL, id), { method: 'DELETE' }, 'DeleteService');
};

export const ExportServicesByProtocol = async (
  protocolId: number,
  filter?: ServiceExportFilter
): Promise<Blob> => {
  try {
    const queryParams = new URLSearchParams();

    if (filter?.DateFrom) {
      queryParams.set('DateFrom', filter.DateFrom);
    }

    if (filter?.DateTo) {
      queryParams.set('DateTo', filter.DateTo);
    }

    if (filter?.SearchText) {
      queryParams.set('SearchText', filter.SearchText);
    }

    if (filter?.PageNumber) {
      queryParams.set('PageNumber', String(filter.PageNumber));
    }

    if (filter?.PageSize) {
      queryParams.set('PageSize', String(filter.PageSize));
    }

    const queryString = queryParams.toString();
    const baseRequestUrl = ProtocolBaseURL + '/' + String(protocolId) + '/services/export';
    const requestUrl = queryString ? baseRequestUrl + '?' + queryString : baseRequestUrl;

    const response = await authFetch(requestUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/octet-stream'
      }
    });

    if (!response.ok) {
      Sentry.captureException(response);
      throw response;
    }

    return response.blob();
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};
